/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_party_roles-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_party_roles-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_party_roles-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_party_roles-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_party_roles-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_party_roles.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_party_roles-entity-config.ts
 * Public fields: 6/6 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupPartyRolesClient, LookupPartyRolesQueryOptions } from '../generated_clients/generated-lookup_party_roles-api-client.js';
import {
  LookupPartyRolesInsert,
  LookupPartyRolesUpdate,
  LookupPartyRolesFieldConfig
} from '../../types/generated_schemas/generated-lookup_party_roles-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_party_roles_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupPartyRolesKeys = {
  all: ['lookup_party_roles'] as const,
  lists: () => [...lookupPartyRolesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupPartyRolesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupPartyRolesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupPartyRolesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupPartyRolesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupPartyRolesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupPartyRolesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupPartyRolesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupPartyRolesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupPartyRolesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupPartyRolesClient();
};

/**
 * Hook to get all lookup_party_roles with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_party_roles data
 */
export const useLookupPartyRolesList = (options?: LookupPartyRolesQueryOptions) => {
  const client = useLookupPartyRolesClient();
  
  return useQuery({
    queryKey: lookupPartyRolesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_party_role by ID
 *
 * @param id - The lookup_party_role ID
 * @returns React Query result with single lookup_party_role data
 */
export const useLookupPartyRoles = (id: string) => {
  const client = useLookupPartyRolesClient();
  
  return useQuery({
    queryKey: lookupPartyRolesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_party_roles
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_party_roles_business_hooks/
 */

/**
 * Hook to create a new lookup_party_role
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_party_role
 */
export const useCreateLookupPartyRoles = () => {
  const client = useLookupPartyRolesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupPartyRolesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPartyRolesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_party_role
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_party_role
 */
export const useUpdateLookupPartyRoles = () => {
  const client = useLookupPartyRolesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupPartyRolesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupPartyRolesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupPartyRolesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_party_role
 *
 * @returns React Query mutation for deleting lookup_party_role
 */
export const useDeleteLookupPartyRoles = () => {
  const client = useLookupPartyRolesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPartyRolesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_party_roles with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupPartyRolesCount = (filter?: Partial<Record<typeof LookupPartyRolesFieldConfig.filterable[number], any>>) => {
  const client = useLookupPartyRolesClient();
  
  return useQuery({
    queryKey: lookupPartyRolesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupPartyRolesFieldConfig = () => {
  return LookupPartyRolesFieldConfig;
};